import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SearchX, BellRing } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { FilterPills } from "@/components/FilterPills";

const STATUS = {
  unmatched: { label: "Still unmatched", cls: "bg-[#B91C1C]/10 text-[#B91C1C]" },
  matched_later: { label: "Matched later", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]" },
  expired: { label: "Expired", cls: "bg-[#1E3F33]/8 text-[#5C6B64]" },
  closed: { label: "Closed", cls: "bg-[#1E3F33]/8 text-[#5C6B64]" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const Row = ({ r }) => (
  <tr className="border-t border-[#1E3F33]/8" data-testid={`unmatched-row-${r.id}`}>
    <td className="px-4 py-3 text-[#5C6B64] whitespace-nowrap">{fmt(r.created_at)}</td>
    <td className="px-4 py-3"><div className="font-medium text-[#1E3F33]">{r.zip_codes.join(", ")}</div><div className="text-[11px] text-[#8A9A86]">{r.state}</div></td>
    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{r.services.map((s) => <span key={s.code} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EFEA] text-[#1E3F33]">{s.label}</span>)}</div></td>
    <td className="px-4 py-3 text-[#5C6B64]">{r.customer_first_name}<div className="text-[11px] text-[#8A9A86]">{r.when_label}</div></td>
    <td className="px-4 py-3 text-[#5C6B64]" data-testid={`unmatched-nearby-${r.id}`}>{r.nearby_collectors} nearby<div className="text-[11px] text-[#8A9A86]">{r.nearby_offering} offer it now</div></td>
    <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS[r.status].cls}`} data-testid={`unmatched-status-${r.id}`}>{STATUS[r.status].label}{r.leads ? ` · ${r.leads}` : ""}</span></td>
  </tr>
);

export default function AdminUnmatched() {
  const [status, setStatus] = useState("unmatched");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/leads/unmatched", { params: { status, start: range.start || undefined, end: range.end || undefined } })
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setData({ rows: [], counts: {} }); });
  }, [status, range]);

  useEffect(() => { load(); }, [load]);
  const c = data?.counts || {};
  const options = [...Object.keys(STATUS).map((k) => ({ key: k, label: `${STATUS[k].label} (${c[k] ?? 0})` })), { key: "all", label: "All" }];

  return (
    <div className="container-x py-12" data-testid="admin-unmatched-page">
      <AdminTabs />
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2"><SearchX className="w-6 h-6 text-[#CC5A3A]" /> Unmatched requests</h1>
      <p className="text-[#5C6B64] mt-2 max-w-3xl">Customer requests that found no collector offering the service nearby when they were submitted. Each one alerts admins by email, SMS and push. A request moves to "Matched later" automatically when a newly active collector covers it.</p>
      <Link to="/admin/account" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#CC5A3A] hover:underline" data-testid="unmatched-alert-settings-link"><BellRing className="w-4 h-4" /> Set where your no-match alerts go</Link>
      <div className="my-6 flex flex-col gap-4">
        <DateRangeFilter onChange={(start, end) => setRange({ start, end })} />
        <FilterPills options={options} value={status} onChange={setStatus} testidPrefix="unmatched-filter" />
      </div>
      {data === null ? <div className="text-[#5C6B64]">Loading…</div> : data.rows.length === 0 ? (
        <div className="card-soft text-sm text-[#8A9A86]" data-testid="unmatched-empty">No requests in this view. Every request found a collector.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10 bg-white">
          <table className="w-full text-sm" data-testid="unmatched-table">
            <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
              {["Submitted", "ZIP", "Services", "Customer", "Collectors", "Status"].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>{data.rows.map((r) => <Row key={r.id} r={r} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
